import { useMemo } from 'react';

import { getMemberIdWithFallback } from '@trello/authentication';
import { useWorkspaceEntitlements } from '@trello/commerce/entitlements';
import { useWaitOnQuery } from '@trello/quickload';

import { EligibleWorkspacesForProvisioningDocument } from './EligibleWorkspacesForProvisioningQuery.generated';
import { type CrossFlowWorkspace } from './useEligibleWorkspacesForProvisioning';

/**
 * Resolves the member's workspaces for the cross-flow eligibility checks in a
 * single place.
 *
 * `useTrelloCrossFlow` calls this once and threads the result through every
 * experience hook, so the provisioning query is subscribed to a single time per
 * tree rather than once per eligibility check (a measured perf bottleneck).
 */
export const useCrossFlowWorkspaces = (): CrossFlowWorkspace[] => {
  const memberId = getMemberIdWithFallback();
  const { data } = useWaitOnQuery(EligibleWorkspacesForProvisioningDocument, {
    variables: { memberId },
    waitOn: ['MemberHeader'],
    context: {
      operationName: 'EligibleWorkspacesForProvisioning',
      document: EligibleWorkspacesForProvisioningDocument,
    },
  });
  const organizations = data?.member?.organizations;
  const { workspaces: entitlements } = useWorkspaceEntitlements();

  return useMemo(() => {
    if (!organizations?.length) {
      return [];
    }

    const entitlementsByWorkspaceId = new Map(
      entitlements.map((entitlement) => [entitlement.id, entitlement]),
    );

    return organizations.map((organization) => ({
      id: organization.id,
      displayName: organization.displayName,
      offering: organization.offering,
      idEnterprise: organization.idEnterprise,
      jwmLink: organization.jwmLink,
      entitlement: entitlementsByWorkspaceId.get(organization.id) ?? null,
    }));
  }, [organizations, entitlements]);
};
